import React from 'react'
import _ from "lodash";
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState, useCallback } from "react";
import {useAuth0} from "@auth0/auth0-react";
import authAxios from "utils/authAxios";
import CircularProgress from '@mui/material/CircularProgress';
import {setAuthorised} from "redux/global/globalSlice";
import {Box} from "@mui/system";
import Logo from "images/TPP_Logo_Rev.svg";
import {fetchUserProfile} from "redux/userProfile/userProfileSlice";
import * as Sentry from '@sentry/react';

const AuthProvider = ({children}) => {
  const { getAccessTokenSilently, isAuthenticated, isLoading, error: auth0Error, loginWithRedirect, logout } = useAuth0();
  const dispatch = useDispatch();
  const isAuthorised = useSelector(state => state.global.isAuthorised);
  const userProfile = useSelector(state => state.userProfile.data);
  const [authError, setAuthError] = useState(null);

  // Handle auth errors by logging to Sentry and redirecting to logout
  useEffect(() => {
    if (auth0Error || authError) {
      const error = auth0Error || authError;
      // Log to Sentry
      Sentry.captureException(error);
      // Redirect to logout
      logout({ returnTo: window.location.origin });
    }
  }, [auth0Error, authError, logout]);

  const initializeAuth = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const accessToken = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_BACKEND_URL,
          scope: 'openid profile email offline_access'
        },
        detailedResponse: true
      });
      
      if(!_.isEmpty(accessToken)) {
        authAxios.defaults.headers.common['Authorization'] = `Bearer ${accessToken.access_token}`;
        dispatch(setAuthorised(true));
        await dispatch(fetchUserProfile());
      }
    } catch (error) {
      
      // Try to refresh the token silently first
      try {
        await getAccessTokenSilently({ ignoreCache: true });
        // If successful, retry initialization
        initializeAuth();
      } catch (refreshError) {
        // Log to Sentry and set error state
        Sentry.captureException(refreshError);
        setAuthError(error);
        // Logout will be handled by the useEffect above
      }
    }
  }, [isAuthenticated, getAccessTokenSilently, dispatch, loginWithRedirect]);

  useEffect(() => {
    let isMounted = true;

    const setupAuth = async () => {
      if (!isMounted) return;
      await initializeAuth();
    };

    setupAuth();
    
    return () => {
      isMounted = false;
    };
  }, [initializeAuth]);

  return (
    <>
      {isLoading || (isAuthenticated && !isAuthorised) || (isAuthorised && userProfile === null) ? (
        <Box sx={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#181720' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <img src={Logo} alt="Portfolio Platform" width={160} style={{ marginLeft: "8px", marginRight: "8px", marginBottom: "20px" }} />
            <CircularProgress color="secondary" />
          </Box>
        </Box>
      ) : (
        children
      )}
    </>
  );
};

export default AuthProvider;
